import React, { ReactElement, useMemo, useRef, useState } from "react";
import { Button, Col, Modal, Radio, Row } from "antd";
import style from "./index.module.scss";
import { BaseQuestionType, QuestionType } from "service/question";
import { errorMessage } from "service/service";
import Form from "rc-field-form";
import { ValidateErrorEntity } from "rc-field-form/es/interface";
import usePromiseCallback from "utils/usePromiseCallback";
import { useUpdateEffect } from "react-use";
import FieldContentText from "./components/FieldContentText";
import FieldOptions from "./components/FieldOptions";
import FieldTypes from "./components/FieldTypes";
import { ResultMessage } from "components/ResultMessage";
import SubmitError from "./components/SubmitError";

interface AddTopicModalProps {
    title: React.ReactNode;
    visible?: boolean;
    onOk: (data: QuestionType) => Promise<void> | void;
    onCancel?: () => void;
    types?: BaseQuestionType[];
    initialValue?: QuestionType;
    afterClose?: () => void;
}

export default function AddTopicModal({
    title,
    visible,
    onOk,
    onCancel,
    types,
    initialValue,
    afterClose
}: AddTopicModalProps) {
    const defaultTopicType = BaseQuestionType.SingleChoice; // 默认选中的题型
    const [queTypeId, setQueTypeId] =
        useState<BaseQuestionType>(defaultTopicType);

    const [form] = Form.useForm<QuestionType>();

    const saveTypeRef = useRef(false); // true保存并关闭 false保存并继续添加
    const [errors, setErrors] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    // 切换题型重置内容
    useUpdateEffect(() => form.resetFields(), [queTypeId]);

    // 提交表单
    const onFinish = usePromiseCallback(async (question: QuestionType) => {
        try {
            setLoading(true);
            setErrors([]);
            await onOk({ ...question, queTypeId: queTypeId as any });

            if (saveTypeRef.current) {
                onCancel?.();
            } else {
                form.resetFields();
                scrollToTop();
            }
        } catch (e) {
            ResultMessage.error(errorMessage(e));
        } finally {
            setLoading(false);
        }
    });

    // 内容区域滚动到最上
    function scrollToTop() {
        document
            .querySelector(`.${style.root} .ant-modal-body`)
            ?.scrollTo(0, 0);
    }

    // 表单校验失败
    function onFinishFailed({
        errorFields
    }: ValidateErrorEntity<QuestionType>) {
        setErrors(errorFields);
        ResultMessage.error(<SubmitError />, { width: 400 });
    }

    // 保存并添加
    async function onSaveAndContinue() {
        saveTypeRef.current = false;
        form.submit();
    }

    // 保存并关闭
    async function onSave() {
        saveTypeRef.current = true;
        form.submit();
    }

    // 获取某一表单项的错误信息
    function getError(name: string) {
        for (let i in errors) {
            for (let j in errors[i].name) {
                if (errors[i].name[j] === name) {
                    return errors[i].errors[j];
                }
            }
        }
    }

    // 如果修改过内容, 关闭前给出提示
    function onClose() {
        if (form.isFieldsTouched()) {
            Modal.confirm({
                content: "页面输入内容未保存，您确定离开此页面吗？",
                onOk: onCancel
            });
        } else {
            onCancel?.();
        }
    }

    const footer = (
        <div>
            <Button onClick={onClose}>取消</Button>
            {!initialValue && (
                <Button
                    type="primary"
                    ghost
                    onClick={onSaveAndContinue}
                    loading={!saveTypeRef.current && loading}>
                    继续添加
                </Button>
            )}
            <Button
                type="primary"
                onClick={onSave}
                loading={saveTypeRef.current && loading}>
                保存
            </Button>
        </div>
    );

    // 如果title中包含onClose属性, 重置onClose的行为
    const modalTitle = useMemo(() => {
        if (typeof title === "string") return title;
        return React.cloneElement(React.Children.only(title) as ReactElement, {
            onClose
        });
    }, [title]);

    return (
        <Modal
            title={modalTitle}
            visible={visible}
            onCancel={onClose}
            footer={footer}
            width={940}
            maskClosable={false}
            centered
            className={style.root}
            afterClose={afterClose}
            destroyOnClose>
            <Form
                form={form}
                onFinish={onFinish}
                onFinishFailed={onFinishFailed}
                initialValues={initialValue}>
                <Form.Field name="id" />
                <Form.Field name="paperId" />
                <Form.Field name="sort" />

                <FieldTypes
                    disabled={!!initialValue}
                    types={types || []}
                    value={queTypeId}
                    onChange={setQueTypeId}
                    showManage={!types}
                    form={form}
                />

                <Row gutter={[0, 20]}>
                    <Col span={4} className={style.label}>
                        是否必填
                    </Col>

                    <Col span={18} offset={1}>
                        <Form.Field name="requiredFlag" initialValue="1">
                            <Radio.Group>
                                <Radio value="0">否</Radio>
                                <Radio value="1">是</Radio>
                            </Radio.Group>
                        </Form.Field>
                    </Col>
                </Row>

                <FieldContentText />
                {[
                    BaseQuestionType.SingleChoice,
                    BaseQuestionType.MultipleChoice
                ].includes(queTypeId) && (
                    <FieldOptions queTypeId={queTypeId} getError={getError} />
                )}
            </Form>
        </Modal>
    );
}
